import { toStringValue } from '../../(common-lib)/Utils';

export const ENTITY_PARTICIPANT_STORAGE_ID = 'entity_participant_items';
export const ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID = 'entity_participant_is_guarantor';
export const ENTITY_PARTICIPANT_ID_FIELD_ID = 'participantId';
export const GUARANTOR_INFORMATION_SECTION_ID = 'guarantor_information';

export type ParticipantEntry = Record<string, unknown>;
export type GuarantorSelection =
  | typeof GUARANTOR_SELECTION_GUARANTOR
  | typeof GUARANTOR_SELECTION_PARTICIPANT
  | typeof GUARANTOR_SELECTION_TBD;

export const GUARANTOR_SELECTION_GUARANTOR = 'Yes';
export const GUARANTOR_SELECTION_PARTICIPANT = 'No';
export const GUARANTOR_SELECTION_TBD = 'To be determined';

export function createParticipantId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  return `participant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getParticipantId(entry: ParticipantEntry | null | undefined): string {
  const existing = toStringValue(entry?.[ENTITY_PARTICIPANT_ID_FIELD_ID]).trim();
  if (existing) return existing;
  return createParticipantId();
}

export function getGuarantorSelection(entry: ParticipantEntry | null | undefined, index?: number): GuarantorSelection {
  if (index === 0) return GUARANTOR_SELECTION_GUARANTOR;

  const current = toStringValue(entry?.[ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID]).trim();
  if (current === GUARANTOR_SELECTION_GUARANTOR) return GUARANTOR_SELECTION_GUARANTOR;
  if (current === GUARANTOR_SELECTION_PARTICIPANT) return GUARANTOR_SELECTION_PARTICIPANT;
  return GUARANTOR_SELECTION_TBD;
}

export const normalizeGuarantorSelection = (entries: ParticipantEntry[]): ParticipantEntry[] => (
  entries.map((entry, index) => {
    // Additional participants should stay undecided until a role is picked explicitly.
    const nextSelection = getGuarantorSelection(entry, index);

    return {
      ...entry,
      [ENTITY_PARTICIPANT_ID_FIELD_ID]: getParticipantId(entry),
      [ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID]: nextSelection,
    };
  })
);

export const buildEmptyParticipantEntry = (fieldIds: string[]): ParticipantEntry => {
  const entry: ParticipantEntry = {};
  fieldIds.forEach((fieldId) => {
    entry[fieldId] = '';
  });
  entry[ENTITY_PARTICIPANT_ID_FIELD_ID] = createParticipantId();
  entry[ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID] = GUARANTOR_SELECTION_TBD;
  return entry;
};

export const isEntryGuarantor = (entry: ParticipantEntry, index: number): boolean => {
  return getGuarantorSelection(entry, index) === GUARANTOR_SELECTION_GUARANTOR;
};

export const setEntryGuarantorSelection = (
  entry: ParticipantEntry,
  index: number,
  selection: GuarantorSelection
): ParticipantEntry => {
  if (index === 0) {
    return {
      ...entry,
      [ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID]: GUARANTOR_SELECTION_GUARANTOR,
    };
  }

  return {
    ...entry,
    [ENTITY_PARTICIPANT_IS_GUARANTOR_FIELD_ID]: selection,
  };
};

export const getParticipantLabel = (index: number): string => {
  if (index === 0) return 'Main Guarantor';
  return `Shareholder ${index + 1}`;
};

export const getParticipantName = (entry: Record<string, unknown>): string => {
  const firstName = toStringValue(entry.entity_participant_first_name).trim();
  const lastName = toStringValue(entry.entity_participant_last_name).trim();
  return [firstName, lastName].filter(Boolean).join(' ').trim();
};
